import React, { useState } from "react";
import { CreditCard, FileText, Shield, Phone, Mail, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InsuranceVerification = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    patientName: "",
    dateOfBirth: "",
    phone: "",
    email: "",
    primaryInsurance: "",
    policyNumber: "",
    groupNumber: "",
    subscriberName: "",
    subscriberDOB: "",
    relationshipToSubscriber: "",
    secondaryInsurance: "",
    secondaryPolicyNumber: "",
    secondaryGroupNumber: "",
    medicaidNumber: "",
    priorAuthRequired: false,
    physicianName: "",
    physicianPhone: "",
    facilityName: "",
    transportationNeeds: "",
    frequencyOfService: "",
    additionalNotes: "",
    consentToVerify: false,
    consentToContact: false
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/insurance-verification`, formData);
      
      if (response.data.success) {
        toast.success("Insurance information submitted! We'll verify your coverage and contact you within 24 hours.");
        // Reset form
        setFormData({
          patientName: "",
          dateOfBirth: "",
          phone: "",
          email: "",
          primaryInsurance: "",
          policyNumber: "",
          groupNumber: "",
          subscriberName: "",
          subscriberDOB: "",
          relationshipToSubscriber: "",
          secondaryInsurance: "",
          secondaryPolicyNumber: "",
          secondaryGroupNumber: "",
          medicaidNumber: "",
          priorAuthRequired: false,
          physicianName: "",
          physicianPhone: "",
          facilityName: "",
          transportationNeeds: "",
          frequencyOfService: "",
          additionalNotes: "",
          consentToVerify: false,
          consentToContact: false
        });
      }
    } catch (error) {
      console.error("Insurance verification error:", error);
      const errorMessage = error.response?.data?.detail || 
                          "There was an error submitting your insurance information. Please call us at (269) 248-3514.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Insurance Verification</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Submit your insurance information to verify coverage for medical transportation services. 
            We'll check your benefits and confirm what's covered under your plan.
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="text-center border-green-200 bg-green-50">
            <CardContent className="p-6">
              <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">HIPAA Compliant</h3>
              <p className="text-gray-600 text-sm">Your information is protected and secure</p>
            </CardContent>
          </Card>
          
          <Card className="text-center border-blue-200 bg-blue-50">
            <CardContent className="p-6">
              <CreditCard className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Most Insurance Accepted</h3>
              <p className="text-gray-600 text-sm">We work with Medicare, Medicaid, and private insurance</p>
            </CardContent>
          </Card>
          
          <Card className="text-center border-purple-200 bg-purple-50">
            <CardContent className="p-6">
              <CheckCircle className="h-12 w-12 text-purple-600 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Fast Verification</h3>
              <p className="text-gray-600 text-sm">Coverage verification within 24 hours</p>
            </CardContent>
          </Card>
        </div>

        {/* Insurance Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900">Insurance Information Form</CardTitle>
            <p className="text-gray-600">
              Please provide complete insurance information for accurate benefit verification
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Patient Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Patient Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="patientName">Patient Full Name *</Label>
                    <Input
                      id="patientName"
                      type="text"
                      value={formData.patientName}
                      onChange={(e) => handleInputChange("patientName", e.target.value)}
                      placeholder="Patient's full legal name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirth">Patient Date of Birth *</Label>
                    <Input
                      id="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      placeholder="(269) 123-4567"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      placeholder="patient.email@example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Primary Insurance */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Primary Insurance</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="primaryInsurance">Insurance Company *</Label>
                    <Select value={formData.primaryInsurance} onValueChange={(value) => handleInputChange("primaryInsurance", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select insurance company" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="medicare">Medicare</SelectItem>
                        <SelectItem value="medicaid">Medicaid</SelectItem>
                        <SelectItem value="blue-cross">Blue Cross Blue Shield</SelectItem>
                        <SelectItem value="priority-health">Priority Health</SelectItem>
                        <SelectItem value="molina">Molina Healthcare</SelectItem>
                        <SelectItem value="united-healthcare">United Healthcare</SelectItem>
                        <SelectItem value="aetna">Aetna</SelectItem>
                        <SelectItem value="humana">Humana</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="policyNumber">Policy/Member ID Number *</Label>
                    <Input
                      id="policyNumber"
                      type="text"
                      value={formData.policyNumber}
                      onChange={(e) => handleInputChange("policyNumber", e.target.value)}
                      placeholder="Policy or member ID number"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="groupNumber">Group Number</Label>
                    <Input
                      id="groupNumber"
                      type="text"
                      value={formData.groupNumber}
                      onChange={(e) => handleInputChange("groupNumber", e.target.value)}
                      placeholder="Group number (if applicable)"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subscriberName">Subscriber Name</Label>
                    <Input
                      id="subscriberName"
                      type="text"
                      value={formData.subscriberName}
                      onChange={(e) => handleInputChange("subscriberName", e.target.value)}
                      placeholder="Primary policyholder name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="subscriberDOB">Subscriber Date of Birth</Label>
                    <Input
                      id="subscriberDOB"
                      type="date"
                      value={formData.subscriberDOB}
                      onChange={(e) => handleInputChange("subscriberDOB", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="relationshipToSubscriber">Relationship to Subscriber</Label>
                    <Select value={formData.relationshipToSubscriber} onValueChange={(value) => handleInputChange("relationshipToSubscriber", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="self">Self</SelectItem>
                        <SelectItem value="spouse">Spouse</SelectItem>
                        <SelectItem value="child">Child</SelectItem>
                        <SelectItem value="parent">Parent</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Secondary Insurance */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Secondary Insurance (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="secondaryInsurance">Secondary Insurance Company</Label>
                    <Input
                      id="secondaryInsurance"
                      type="text"
                      value={formData.secondaryInsurance}
                      onChange={(e) => handleInputChange("secondaryInsurance", e.target.value)}
                      placeholder="Secondary insurance name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="secondaryPolicyNumber">Secondary Policy Number</Label>
                    <Input
                      id="secondaryPolicyNumber"
                      type="text"
                      value={formData.secondaryPolicyNumber}
                      onChange={(e) => handleInputChange("secondaryPolicyNumber", e.target.value)}
                      placeholder="Secondary policy number"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="secondaryGroupNumber">Secondary Group Number</Label>
                    <Input
                      id="secondaryGroupNumber"
                      type="text"
                      value={formData.secondaryGroupNumber}
                      onChange={(e) => handleInputChange("secondaryGroupNumber", e.target.value)}
                      placeholder="Secondary group number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="medicaidNumber">Medicaid Number (if applicable)</Label>
                    <Input
                      id="medicaidNumber"
                      type="text"
                      value={formData.medicaidNumber}
                      onChange={(e) => handleInputChange("medicaidNumber", e.target.value)}
                      placeholder="Medicaid number"
                    />
                  </div>
                </div>
              </div>

              {/* Healthcare Provider Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Healthcare Provider Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="physicianName">Physician/Provider Name</Label>
                    <Input
                      id="physicianName"
                      type="text"
                      value={formData.physicianName}
                      onChange={(e) => handleInputChange("physicianName", e.target.value)}
                      placeholder="Dr. Smith or ABC Medical Group"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="physicianPhone">Provider Phone Number</Label>
                    <Input
                      id="physicianPhone"
                      type="tel"
                      value={formData.physicianPhone}
                      onChange={(e) => handleInputChange("physicianPhone", e.target.value)}
                      placeholder="(269) 123-4567"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="facilityName">Facility/Clinic Name</Label>
                  <Input
                    id="facilityName"
                    type="text"
                    value={formData.facilityName}
                    onChange={(e) => handleInputChange("facilityName", e.target.value)}
                    placeholder="Hospital, clinic, or medical facility name"
                  />
                </div>
              </div>

              {/* Service Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Transportation Service Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="transportationNeeds">Transportation Needs</Label>
                    <Select value={formData.transportationNeeds} onValueChange={(value) => handleInputChange("transportationNeeds", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select transportation type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="wheelchair">Wheelchair Accessible</SelectItem>
                        <SelectItem value="ambulatory">Ambulatory (Walking)</SelectItem>
                        <SelectItem value="both">May need both options</SelectItem>
                        <SelectItem value="unsure">Unsure - need assessment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="frequencyOfService">Frequency of Service</Label>
                    <Select value={formData.frequencyOfService} onValueChange={(value) => handleInputChange("frequencyOfService", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="How often do you need transportation?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="one-time">One-time appointment</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="bi-weekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="multiple-weekly">Multiple times per week</SelectItem>
                        <SelectItem value="as-needed">As needed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="additionalNotes">Additional Notes</Label>
                  <Input
                    id="additionalNotes"
                    type="text"
                    value={formData.additionalNotes}
                    onChange={(e) => handleInputChange("additionalNotes", e.target.value)}
                    placeholder="Any special requirements or additional information"
                  />
                </div>
              </div>

              {/* Prior Authorization */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Prior Authorization</h3>
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="priorAuthRequired"
                    checked={formData.priorAuthRequired}
                    onCheckedChange={(checked) => handleInputChange("priorAuthRequired", checked)}
                  />
                  <Label htmlFor="priorAuthRequired" className="text-sm leading-relaxed">
                    My insurance requires prior authorization for medical transportation (check if you know this applies)
                  </Label>
                </div>
              </div>

              {/* Consent */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Authorization & Consent</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="consentToVerify"
                      checked={formData.consentToVerify}
                      onCheckedChange={(checked) => handleInputChange("consentToVerify", checked)}
                    />
                    <Label htmlFor="consentToVerify" className="text-sm leading-relaxed">
                      I authorize Quality Transport Services to verify my insurance benefits and coverage for medical transportation services. I understand this may involve contacting my insurance company and healthcare providers. *
                    </Label>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="consentToContact"
                      checked={formData.consentToContact}
                      onCheckedChange={(checked) => handleInputChange("consentToContact", checked)}
                    />
                    <Label htmlFor="consentToContact" className="text-sm leading-relaxed">
                      I consent to be contacted by Quality Transport Services regarding my insurance verification and transportation services via phone, email, or mail at the contact information provided. *
                    </Label>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-blue-900 hover:bg-blue-800"
                  disabled={isSubmitting || !formData.consentToVerify || !formData.consentToContact}
                >
                  {isSubmitting ? "Submitting Information..." : "Submit Insurance Information"}
                </Button>
                <p className="text-sm text-gray-500 mt-3 text-center">
                  We'll verify your coverage and contact you within 24 hours with benefit information.
                  All information is kept confidential and HIPAA compliant.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">Need help with insurance questions?</p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6">
            <div className="flex items-center space-x-2">
              <Phone className="h-4 w-4 text-blue-900" />
              <a href="tel:(269) 248-3514" className="font-semibold text-blue-900 hover:text-blue-700">
                (269) 248-3514
              </a>
            </div>
            <div className="flex items-center space-x-2">
              <Mail className="h-4 w-4 text-blue-900" />
              <a href="mailto:qualitytransportservices42@gmail.com" className="text-blue-900 hover:text-blue-700">
                qualitytransportservices42@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsuranceVerification;